import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import withLayout from "../layout";
import RentHeader, { RentFooter } from "../rent/RentHeader";
import { getLocation } from "../rent/api";
import { euro, fmtDate, fmtTime, isNightType, isSubscription, suggestNow } from "../rent/tariffs";
import { Icons, tariffName, tariffWhen, unitLabel } from "../rent/ui";
import "../login/login.css";

const TariffCard = ({ tariff, onPick, t, lang }) => {
  const soldOut = tariff.free === 0;
  return (
    <button type="button" className="rx-tariff" onClick={() => onPick(tariff)} disabled={soldOut}>
      <div className={`rx-ic ${isNightType(tariff) ? "night" : ""}`}>{Icons[tariff.type]}</div>
      <h3>{tariffName(tariff, t)}</h3>
      <p className="rx-when">{tariffWhen(tariff, t, lang)}</p>
      <div className="rx-pr">
        <b>{euro(tariff.price, lang)}</b>
        <span>{unitLabel(tariff, t)}</span>
      </div>
      {tariff.free !== null && (
        <div className={`rx-avail ${soldOut ? "none" : ""}`}>
          {soldOut ? t("rxSoldOut") : t(isSubscription(tariff) ? "rxSubsFree" : "rxSpacesFree", { count: tariff.free })}
        </div>
      )}
    </button>
  );
};

// Standortseite – Einstieg aus der Suche oder direkt per QR-Code (/p/:id?qr=1)
const SitePage = ({ setLoaderBox }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id } = useParams();
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const [state, setState] = useState({ loading: true, location: null, error: false });
  const [img, setImg] = useState(0);
  const fromQr = query.get("qr") === "1";
  const fromSearch = query.get("from");

  useEffect(() => {
    let alive = true;
    setLoaderBox(true);
    getLocation(id, { activeTab: query.get("activeTab") || "parking-hourly" })
      .then((location) => alive && setState({ loading: false, location, error: false }))
      .catch(() => alive && setState({ loading: false, location: null, error: true }))
      .finally(() => alive && setLoaderBox(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loc = state.location;
  useEffect(() => {
    document.title = `${loc ? loc.title : "Parkplatz"} | innopark24`;
  }, [loc]);

  const now = useMemo(() => (fromQr && loc ? suggestNow(loc) : null), [fromQr, loc]);

  const book = (tariff, extra = {}) => {
    const params = new URLSearchParams({ ...extra });
    ["activeTab", "parkingFrom", "parkingUntil"].forEach((k) => query.get(k) && !extra.now && params.set(k, query.get(k)));
    if (fromSearch) params.set("from", fromSearch);
    navigate(`/buchen/${encodeURIComponent(loc.id)}/${encodeURIComponent(tariff.id)}?${params.toString()}`);
  };

  const back = fromSearch ? (
    <Link className="rx-back" to={`/search?${fromSearch}`}>
      {Icons.back}
      {t("rxBackResults")}
    </Link>
  ) : (
    <Link className="rx-back" to="/">
      {Icons.back}
      {t("rxBackStart")}
    </Link>
  );

  if (state.error) {
    return (
      <div className="rp">
        <RentHeader />
        <main className="rp-main">
          {back}
          <div className="rx-card rx-done" style={{ borderTopColor: "var(--ipl-blue)" }}>
            <h1>{t("rxNotFoundHead")}</h1>
            <p className="rx-muted">{t("rxNotFoundText", { code: id })}</p>
            <div className="rx-actions" style={{ marginTop: 18 }}>
              <Link className="ipl-btn" to="/vor-ort">
                <span>{t("rxTryAgain")}</span>
              </Link>
            </div>
          </div>
        </main>
        <RentFooter />
      </div>
    );
  }

  const singles = loc ? loc.tariffs.filter((x) => !isSubscription(x)) : [];
  const subs = loc ? loc.tariffs.filter(isSubscription) : [];

  let nowText = "";
  if (now) {
    if (now.window) nowText = t("rxNowWindow", { name: tariffName(now.tariff, t), from: `${fmtDate(now.window.start, lang)} ${fmtTime(now.window.start, lang)}`, until: `${fmtDate(now.window.end, lang)} ${fmtTime(now.window.end, lang)}` });
    else if (now.from) nowText = t("rxNowHour", { from: fmtTime(now.from, lang) });
    else nowText = t("rxNowDay", { name: tariffName(now.tariff, t) });
  }

  return (
    <div className="rp">
      <RentHeader />
      <main className="rp-main">
        {back}
        {loc && (
          <>
            <div className="rx-site-head">
              <div className="rx-gallery">
                {loc.images.length > 0 ? <img src={loc.images[img]} alt={loc.title} /> : <span className="rx-p">P</span>}
                {loc.images.length > 1 && (
                  <>
                    <button type="button" className="rx-nav-btn rx-prev" aria-label={t("rxPrevImage")} onClick={() => setImg((img - 1 + loc.images.length) % loc.images.length)}>
                      {Icons.back}
                    </button>
                    <button type="button" className="rx-nav-btn rx-next" aria-label={t("rxNextImage")} onClick={() => setImg((img + 1) % loc.images.length)}>
                      {Icons.back}
                    </button>
                    <span className="rx-cnt">{`${img + 1} / ${loc.images.length}`}</span>
                  </>
                )}
              </div>
              <div className="rx-card rx-site-info">
                <span className="rx-eyebrow">{t("rxSiteEyebrow", { code: loc.code || loc.id })}</span>
                <h1>{loc.title}</h1>
                <p className="rx-muted">{loc.address}</p>
                <div className="rx-facts">
                  {loc.openingHours && (
                    <div className="rx-fact">
                      <span>{t("rxOpen")}</span>
                      <b>{loc.openingHours}</b>
                    </div>
                  )}
                  {loc.spaces !== null && (
                    <div className="rx-fact">
                      <span>{t("rxSpaces")}</span>
                      <b>{loc.free !== null ? t("rxSpacesOf", { total: loc.spaces, free: loc.free }) : loc.spaces}</b>
                    </div>
                  )}
                  <div className="rx-fact">
                    <span>{t("rxEntry")}</span>
                    <b>{t("rxEntryCamera")}</b>
                  </div>
                  {loc.maxHeight !== null && (
                    <div className="rx-fact">
                      <span>{t("rxMaxHeight")}</span>
                      <b>{`${loc.maxHeight.toLocaleString(lang)} m`}</b>
                    </div>
                  )}
                </div>
                {loc.note && (
                  <p className="rx-note">
                    {Icons.info}
                    <span>{loc.note}</span>
                  </p>
                )}
              </div>
            </div>

            {now && (
              <div className="rx-now">
                <div className="rx-ic">{Icons[now.tariff.type]}</div>
                <div>
                  <p>{t("rxNowEyebrow")}</p>
                  <b>{nowText}</b>
                  <p>{t("rxNowHint")}</p>
                </div>
                <button type="button" className="ipl-btn" onClick={() => book(now.tariff, { now: "1" })}>
                  <span>{now.from ? t("rxNowStart") : t("rxPayAmount", { amount: euro(now.tariff.price, lang) })}</span>
                </button>
              </div>
            )}

            <div className="rx-sec-h">
              <h2>{t("rxTariffsHead")}</h2>
              <span className="rx-muted">{t("rxTariffsSub")}</span>
            </div>

            {loc.tariffs.length === 0 && <div className="rx-empty-box">{t("rxNoTariffs")}</div>}

            {singles.length > 0 && (
              <>
                <div className="rx-grp">{t("rxGroupSingle")}</div>
                <div className="rx-tariffs">
                  {singles.map((x) => (
                    <TariffCard key={x.id} tariff={x} onPick={book} t={t} lang={lang} />
                  ))}
                </div>
              </>
            )}
            {subs.length > 0 && (
              <>
                <div className="rx-grp">{t("rxGroupSubs")}</div>
                <div className="rx-tariffs">
                  {subs.map((x) => (
                    <TariffCard key={x.id} tariff={x} onPick={book} t={t} lang={lang} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
      <RentFooter />
    </div>
  );
};

export default withLayout(SitePage);
